/**
 * @file Shared display formatting helpers for Calendar UI surfaces.
 */

import { formatDate } from "../../shared/utils/dateUtils.js";

function getEntryTitle(entry = {}) {
    return entry.title || "Untitled";
}

/**
 * Formats an HH:mm time as compact 12-hour text.
 * @param {string|null|undefined} time Stored HH:mm time.
 * @returns {string} Short display time.
 */
export function formatShortCalendarTime(time) {
    const match = /^(\d{2}):(\d{2})$/.exec(String(time || ""));
    if (!match) {return "";}
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (hours > 23 || minutes > 59) {return "";}
    const period = hours >= 12 ? "pm" : "am";
    const hour12 = hours % 12 || 12;
    return minutes > 0 ?
        `${hour12}.${String(minutes).padStart(2, "0")}${period}` :
        `${hour12}${period}`;
}

/**
 * Formats a Calendar date key for display.
 * @param {string|null|undefined} dateKey Stored YYYY-MM-DD date.
 * @returns {string} Display date.
 */
export function formatCalendarDisplayDate(dateKey) {
    return formatDate(dateKey) || dateKey || "";
}

/**
 * Formats an inclusive Calendar date range.
 * @param {string|null|undefined} startDate Start date.
 * @param {string|null|undefined} endDate End date.
 * @returns {string} Display date range.
 */
export function formatCalendarDisplayDateRange(startDate, endDate) {
    const start = formatCalendarDisplayDate(startDate);
    const end = formatCalendarDisplayDate(endDate || startDate);
    if (!start) {return "";}
    return !end || start === end ? start : `${start} to ${end}`;
}

/**
 * Formats a Calendar time range.
 * @param {string|null|undefined} startTime Start time.
 * @param {string|null|undefined} endTime End time.
 * @returns {string} Display time range.
 */
export function formatCalendarDisplayTimeRange(startTime, endTime) {
    const start = formatShortCalendarTime(startTime);
    const end = formatShortCalendarTime(endTime);
    return [start, end].filter(Boolean).join(" - ");
}

/**
 * Returns the compact visible label for a Calendar entry token.
 * @param {object} entry Calendar entry.
 * @returns {string} Token label.
 */
export function formatCalendarEntryTokenLabel(entry = {}) {
    const title = getEntryTitle(entry);
    if (
        entry.sourceType === "event" &&
        entry.isFullDay !== true &&
        entry.startTime
    ) {
        const startTime = formatShortCalendarTime(entry.startTime);
        return startTime ? `${startTime} ${title}` : title;
    }
    return title;
}

/**
 * Returns a type-specific tooltip for a Calendar entry token.
 * @param {object} entry Calendar entry.
 * @returns {string} Tooltip text.
 */
export function formatCalendarEntryTooltip(entry = {}) {
    const title = getEntryTitle(entry);
    const startDate = entry.startDate || "";
    const endDate = entry.endDate || startDate;
    const dateRange = formatCalendarDisplayDateRange(startDate, endDate);

    if (entry.sourceType === "reminder" || entry.renderKind === "reminder") {
        return [title, dateRange]
            .filter(Boolean)
            .join("\n");
    }

    if (entry.sourceType === "leave" || entry.renderKind === "leave") {
        return [title, dateRange].filter(Boolean).join("\n");
    }

    if (startDate !== endDate) {
        return [title, dateRange].filter(Boolean).join("\n");
    }

    if (entry.isFullDay === true) {
        return [title, `${formatCalendarDisplayDate(startDate)} - All day`]
            .filter(Boolean)
            .join("\n");
    }

    const timeRange = formatCalendarDisplayTimeRange(entry.startTime, entry.endTime);
    return [
        title,
        [formatCalendarDisplayDate(startDate), timeRange].filter(Boolean).join(", "),
    ].filter(Boolean).join("\n");
}
